import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DislikeOutlined, TeamOutlined as GroupOutlineIcon, LikeOutlined } from '@ant-design/icons';
import { Badge, Divider, Popover, Spin as AntdSpin, Tooltip, Typography, Button } from 'antd';

import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import ptBRLocale from 'date-fns/locale/pt-BR';
import { useModerateAffiliate } from 'v2/hooks/useModerateAffiliate';
import { usePendingAffiliatesSummaryCount } from 'v2/hooks/usePendingAffiliatesSummaryCount';
import { usePendingAffiliatesSummaryList } from 'v2/hooks/usePendingAffiliatesSummaryList';

import Topbar from '@eduzz/ui-layout/Topbar';

const POLLING_INTERVAL_MS = 300000;
const MAX_VISIBLE_AFFILIATIONS = 4;

const AffiliationsNotification = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    summaryList,
    isLoading: isLoadingSummaryList,
    refetch: refetchSummaryList
  } = usePendingAffiliatesSummaryList();
  const notificationsShortList = useMemo(() => summaryList?.list?.slice(0, MAX_VISIBLE_AFFILIATIONS), [summaryList]);
  const {
    summaryCount,
    isLoading: isLoadingSummaryCount,
    refetch: refetchSummaryCount
  } = usePendingAffiliatesSummaryCount();
  const isAllLoading = isLoadingSummaryList && isLoadingSummaryCount;

  const { moderateAffiliate } = useModerateAffiliate();

  useEffect(() => {
    refetchSummaryCount();

    const intervalId = setInterval(() => {
      refetchSummaryCount();
    }, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetchSummaryCount]);

  const handleModerateAffiliate = useCallback(
    async (affiliateId: number, status: 'A' | 'R') => {
      const clearCache = (summaryList?.list || []).length - 1 === 0 && (summaryCount?.count || 0) - 1 > 0;

      await moderateAffiliate({ affiliateId, status });
      await refetchSummaryCount();
      await refetchSummaryList(clearCache);
    },
    [moderateAffiliate, refetchSummaryCount, refetchSummaryList, summaryList?.list, summaryCount?.count]
  );

  const handleClose = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen(open);

      if (open) {
        refetchSummaryList();
      }
    },
    [refetchSummaryList]
  );

  const popoverContent = (
    <div className='min-w-[280px]'>
      <Typography.Title level={5} className='mb-0! py-2 text-center'>
        Solicitações de afiliação
      </Typography.Title>
      <Divider className='my-0!' />
      {((summaryList && isLoadingSummaryList) || isAllLoading) && (
        <div className='min-w-[280px] py-3 text-center'>
          <Spin />
        </div>
      )}

      {!isAllLoading &&
        !isLoadingSummaryList &&
        notificationsShortList?.map((affiliation, index) => {
          return (
            <div
              className='flex items-center justify-between px-4 py-2 text-sm text-[#333] hover:bg-[#f9f9f9]'
              key={'notifications-' + index}
            >
              <Link to={'/producer/myaffiliates'} onClick={handleClose}>
                <div className='text-[#333]'>
                  <div className='mb-1 block font-medium'>
                    <Truncate count={25} final='...'>
                      {affiliation.affiliate_name}
                    </Truncate>
                  </div>
                  <div className='mb-1 block text-[#999]'>
                    <Truncate count={25} final='...'>
                      {affiliation.product_title}
                    </Truncate>
                  </div>
                  <div className='text-xs text-[#999]'>
                    {affiliation.request_date &&
                      formatDistanceToNow(new Date(affiliation.request_date), { addSuffix: true, locale: ptBRLocale })}
                  </div>
                </div>
              </Link>

              <div className='ml-4'>
                <Tooltip title='Aprovar' placement='top'>
                  <Button
                    type='text'
                    className='text-[#009358]!'
                    shape='circle'
                    icon={<LikeOutlined />}
                    onClick={() => handleModerateAffiliate(affiliation.id, 'A')}
                  />
                </Tooltip>
                <Tooltip title='Reprovar' placement='top'>
                  <Button
                    type='text'
                    className='text-[#333]!'
                    shape='circle'
                    icon={<DislikeOutlined />}
                    onClick={() => handleModerateAffiliate(affiliation.id, 'R')}
                  />
                </Tooltip>
              </div>
            </div>
          );
        })}

      {!isAllLoading && !isLoadingSummaryList && !notificationsShortList?.length && (
        <div className='p-4 text-center text-sm text-[#333]'>Nenhuma solicitação de afiliação</div>
      )}

      <Divider className='my-0!' />

      <Link to={'/producer/myaffiliates'} onClick={handleClose}>
        <span className='block w-full p-2 text-center text-[13px] font-medium text-[#333]'>VER TODOS</span>
      </Link>
    </div>
  );

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={handleOpenChange}
      content={popoverContent}
      trigger='click'
      placement='bottom'
    >
      <Tooltip title='Solicitações de afiliação' placement='bottom' open={popoverOpen ? false : undefined}>
        <span>
          <Topbar.Action
            id='topbar-action-affiliates-requests'
            aria-label='mostrar solicitações'
            icon={
              isLoadingSummaryCount ? (
                <AntdSpin size='small' />
              ) : (
                <Badge count={summaryCount?.count} offset={[-4, 5]}>
                  <GroupOutlineIcon style={{ fontSize: 24 }} />
                </Badge>
              )
            }
          />
        </span>
      </Tooltip>
    </Popover>
  );
};

export default AffiliationsNotification;
